import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type ConfirmCancelRecord = {
  id: string;
  number?: string | null;
  title?: string | null;
  apPayeeName?: string | null;
  /** Pre-formatted display string; callers format at click time (list vs drawer rules differ). */
  totalAmount: string;
};

type ConfirmCancelModalContextType = {
  onOpenConfirmCancelModal: (record: ConfirmCancelRecord) => void;
  onCloseConfirmCancelModal: () => void;
  cancelModalOpen: boolean;
  cancelRecord: ConfirmCancelRecord | null;
  refetch: () => void | Promise<unknown>;
  onDrawerClose: () => void;
  selectedId: string | null;
};

const Context = createContext<ConfirmCancelModalContextType>({
  onOpenConfirmCancelModal: () => {},
  onCloseConfirmCancelModal: () => {},
  cancelModalOpen: false,
  cancelRecord: null,
  refetch: () => Promise.resolve(),
  onDrawerClose: () => {},
  selectedId: null,
});

type ConfirmCancelModalProviderProps = PropsWithChildren<{
  refetch: () => void | Promise<unknown>;
  onDrawerClose: () => void;
  selectedId: string | null;
}>;

export const ConfirmCancelModalProvider: FC<
  ConfirmCancelModalProviderProps
> = ({ children, refetch, onDrawerClose, selectedId }) => {
  const [cancelRecord, setCancelRecord] = useState<ConfirmCancelRecord | null>(
    null
  );
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const onOpenConfirmCancelModal = useCallback(
    (record: ConfirmCancelRecord) => {
      setCancelRecord(record);
      setCancelModalOpen(true);
    },
    []
  );

  const onCloseConfirmCancelModal = useCallback(() => {
    setCancelModalOpen(false);
    setCancelRecord(null);
  }, []);

  const value = useMemo(
    () => ({
      onOpenConfirmCancelModal,
      onCloseConfirmCancelModal,
      cancelModalOpen,
      cancelRecord,
      refetch,
      onDrawerClose,
      selectedId,
    }),
    [
      onOpenConfirmCancelModal,
      onCloseConfirmCancelModal,
      cancelModalOpen,
      cancelRecord,
      refetch,
      onDrawerClose,
      selectedId,
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

ConfirmCancelModalProvider.displayName = 'ConfirmCancelModalProvider';

export const useConfirmCancelModal = () => useContext(Context);
